import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarClock, Check, X } from 'lucide-react';

interface AlterarDataModalProps {
    /** Quantidade de visitas selecionadas. */
    count: number;
    /** Data de referência (dia atualmente exibido), YYYY-MM-DD. */
    dataAtual: string;
    salvando: boolean;
    onConfirm: (novaData: string) => void;
    onClose: () => void;
}

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return isoDate(d); };
const fmtLongo = (iso: string) => new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });

/** Reagendamento em lote de visitas (Rotas v2): calendário + atalhos rápidos. */
const AlterarDataModal: React.FC<AlterarDataModalProps> = ({ count, dataAtual, salvando, onConfirm, onClose }) => {
    const hoje = isoDate(new Date());
    const [data, setData] = useState(addDays(1));

    const atalhos: Array<{ label: string; value: string }> = [
        { label: 'Hoje', value: hoje },
        { label: 'Amanhã', value: addDays(1) },
        { label: 'Depois de amanhã', value: addDays(2) },
        { label: 'Próxima semana', value: addDays(7) },
    ];

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-xl shrink-0 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                            <CalendarClock size={18} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">Alterar Data da Visita</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {count} {count === 1 ? 'visita será movida' : 'visitas serão movidas'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        De <b className="text-gray-700 dark:text-gray-200">{fmtLongo(dataAtual)}</b> para:
                    </p>

                    <div className="grid grid-cols-2 gap-1.5">
                        {atalhos.map(a => (
                            <button
                                key={a.label}
                                onClick={() => setData(a.value)}
                                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${data === a.value
                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>

                    <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ou escolha no calendário</label>
                        <input
                            type="date"
                            value={data}
                            min={hoje}
                            onChange={e => setData(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                    </div>

                    <p className="text-[11px] text-gray-400 capitalize">📅 {fmtLongo(data)}</p>
                    <p className="text-[11px] text-gray-400">A alteração fica registrada no histórico do lead (auditoria).</p>

                    <button
                        onClick={() => data && onConfirm(data)}
                        disabled={!data || salvando}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                    >
                        <Check size={16} />
                        {salvando ? 'Salvando...' : 'Confirmar nova data'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AlterarDataModal;
